/* eslint-disable react/no-multi-comp */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ArrowDownIcon,
  Box,
  Divider,
  Stack,
  Typography,
  useBreakpoint,
  useMobileBreakpoint,
} from '@customink/pigment-react';
import {
  useConvertProject,
  useDesktopBreakpoint,
  useProject,
} from '@customink/project-service-react-components';
import { SHIPPING_TYPES } from '@customink/project-service-types';
import {
  type ReactNode,
  type SyntheticEvent,
  useEffect,
  useState,
} from 'react';

import OrderSubmit from './OrderSubmit';
import CheckoutError from '../Alerts/CheckoutError';
import { ReorderLineItem } from '../LineItem/ReorderLineItem';
import { Satisfaction } from '../LineItem/Satisfaction';

import { DeliveryLineItem } from '@/components/LineItem/DeliveryLineItem';
import { DeliveryMultiShipLineItem } from '@/components/LineItem/DeliveryMultiShipLineItem';
import { OrderTotal } from '@/components/LineItem/OrderTotal';
import { SubtotalLineItem } from '@/components/LineItem/SubtotalLineItem';
import { TaxLineItem } from '@/components/LineItem/TaxLineItem';
import { VolumeDiscountLineItem } from '@/components/LineItem/VolumeDiscountLineItem';
import { VoucherInputLineItem } from '@/components/LineItem/VoucherInputLineItem';
import { VoucherLineItem } from '@/components/LineItem/VoucherLineItem';
import { useZipCodeValid } from '@/components/ProjectItemList/hooks';
import { useOrderDiscountTotal } from '@/hooks/Pricing';
import { useProjectTotalQuantity } from '@/hooks/Project';
import {
  PgmntColorNeutralBackgroundPrimary,
  PgmntColorNeutralBorderDefault,
  PgmntRadiusCard,
  spacing8,
} from '@/utils/designTokens';
import './OrderSummary.css';

// #region subcomponents -> fold me to skip to main component
type WrapperProps = {
  children: ReactNode;
};

const Wrapper = ({ children }: WrapperProps) => {
  const isDesktop = useDesktopBreakpoint();
  const isMobile = useMobileBreakpoint();
  const totalQuantity = useProjectTotalQuantity();
  const { orderTotal } = useOrderDiscountTotal();
  const { zipCodeValid } = useZipCodeValid();
  const [, { isError: isCheckoutError }] = useConvertProject();

  const [expanded, setExpanded] = useState(false);

  const handleChange = (_: SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  useEffect(() => {
    if (!zipCodeValid) {
      setExpanded(true);
    }
  }, [zipCodeValid]);

  return isDesktop ? (
    <Stack
      data-testid="order-summary"
      gap="20px"
      sx={{
        padding: '24px',
        borderRadius: PgmntRadiusCard,
        backgroundColor: PgmntColorNeutralBackgroundPrimary,
        border: `1px solid ${PgmntColorNeutralBorderDefault}`,
      }}
    >
      <Typography marginBottom="-4px" variant="heading3">
        Order Summary
      </Typography>
      {children}
      <OrderSubmit />
      {isCheckoutError && <CheckoutError />}
    </Stack>
  ) : (
    <Box data-testid="order-summary">
      <Accordion
        data-testid="order-summary-accordion"
        expanded={expanded}
        onChange={handleChange}
        sx={{
          backgroundColor: 'transparent',
          borderRadius: 'none',
          boxShadow: 'none',
        }}
      >
        <AccordionSummary
          expandIcon={<ArrowDownIcon />}
          style={{ padding: 0, minHeight: 'unset' }}
        >
          <Box
            alignItems="center"
            display="flex"
            gap={spacing8}
            justifyContent="space-between"
            margin="4px 0"
            width="100%"
          >
            <Typography variant="heading3" whiteSpace="nowrap">
              Order Summary
            </Typography>
            <Typography
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                flex: 1,
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: '0px 6px',
                marginRight: '2px',
              }}
              variant="uiTextDefault"
            >
              {totalQuantity}&nbsp;items
              <Divider
                orientation="vertical"
                sx={{
                  borderColor: PgmntColorNeutralBorderDefault,
                  height: '16px',
                }}
              />
              ${orderTotal}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: '4px 8px 20px' }}>
          <Stack gap="20px">{children}</Stack>
        </AccordionDetails>
      </Accordion>
      {isCheckoutError && <CheckoutError />}
      {isMobile && (
        <Divider
          sx={{
            borderColor: PgmntColorNeutralBorderDefault,
            margin: `0 -8px`,
          }}
        />
      )}
    </Box>
  );
};
// #endregion

export function OrderSummary() {
  const { data: project } = useProject();
  const shippingType = project?.configuration?.shippingType;
  const isDesktop = useDesktopBreakpoint();
  const isMdBreakpoint = useBreakpoint('md');

  return (
    <div className="pje-CartOrderSummary">
      <Wrapper>
        <Stack spacing={1.5} useFlexGap>
          <SubtotalLineItem />
          <VolumeDiscountLineItem />
          <ReorderLineItem />
          {shippingType === SHIPPING_TYPES.INDIVIDUAL ? (
            <DeliveryMultiShipLineItem />
          ) : (
            <DeliveryLineItem />
          )}
          <TaxLineItem />
          <VoucherLineItem />
          <VoucherInputLineItem />
        </Stack>
        <Divider />
        <OrderTotal />
      </Wrapper>
      <Box
        sx={{
          margin: isMdBreakpoint ? '0 0 16px' : isDesktop ? '20px 0' : '16px 0',
        }}
      >
        <Satisfaction compact={!isDesktop} />
      </Box>
    </div>
  );
}
